var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { message, Tooltip } from 'antd';
import { Permissions } from '@whatsper/texterchat-common';
import { reaction } from 'mobx';
import { useStore } from '../../../store/rootStore';
import ChatNotesHeader from './Components/ChatNotesHeader';
import ChatNoteInput from './Components/ChatNoteInput';
import ChatNotesList from './Components/ChatNotesList';
import { Trans } from 'react-i18next';
import styles from './ChatNotes.module.scss';
const ChatNotes = ({ scrollToNote }) => {
    const store = useStore();
    const [noteText, setNoteText] = useState('');
    const [editingNote, setEditingNote] = useState(null);
    const [collapseNotes, setCollapseNotes] = useState(false);
    const [loading, setLoading] = useState(false);
    const [highlightedNoteId, setHighlightedNoteId] = useState(null);
    const notesContainerRef = useRef(null);
    const inputRef = useRef(null);
    const { activeChatStore, chatNotesStore } = store;
    const handleOnClickNote = useCallback((note) => {
        if (scrollToNote && note.messageId) {
            scrollToNote(String(note._id), note.messageId, true);
        }
        setEditingNote(null);
        setNoteText('');
    }, [scrollToNote]);
    const toggleCollapseNotes = useCallback(() => {
        setCollapseNotes((prev) => !prev);
    }, []);
    const handleChangeNoteText = useCallback((newNoteText) => {
        setNoteText(newNoteText);
    }, []);
    const handleAddNote = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            yield store.chatNotesStore.addChatNote(noteText);
            setNoteText('');
            message.success(React.createElement(Trans, { i18nKey: "noteAddSuccess" }));
        }
        catch (error) {
            message.error(React.createElement(Trans, { i18nKey: "noteAddError" }));
            console.error(`Couldn't add the chat note: ${error.message}`);
        }
        finally {
            setLoading(false);
        }
    }), [noteText, store.chatNotesStore]);
    const handleUpdateNote = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!editingNote)
            return;
        setLoading(true);
        try {
            yield store.chatNotesStore.updateChatNote(String(editingNote._id), { text: noteText });
            setNoteText('');
            setEditingNote(null);
            message.success(React.createElement(Trans, { i18nKey: "noteUpdateSuccess" }));
        }
        catch (error) {
            message.error(React.createElement(Trans, { i18nKey: "noteUpdateError" }));
            console.error(`Couldn't update the chat note: ${error.message}`);
        }
        finally {
            setLoading(false);
        }
    }), [noteText, store.chatNotesStore, editingNote]);
    const handleSelectNoteToEdit = useCallback((note) => {
        var _a;
        setEditingNote(note);
        setNoteText(note.text);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    const handleCancelEdit = useCallback(() => {
        setEditingNote(null);
        setNoteText('');
    }, []);
    const getAgent = useCallback((note) => {
        const agent = store.agentsStore.usersMap.get(note.createdBy);
        return agent || null;
    }, [store.agentsStore.usersMap]);
    const checkAgentPermission = useCallback((note) => {
        const noteAgent = getAgent(note);
        const { userDetails } = store.loginStore;
        const { hasPermissions } = store.agentsStore;
        return (hasPermissions([Permissions.Permission.chats_notes_all_manage]) ||
            (hasPermissions([Permissions.Permission.chats_notes_own_manage]) && (noteAgent === null || noteAgent === void 0 ? void 0 : noteAgent._id) === (userDetails === null || userDetails === void 0 ? void 0 : userDetails._id)));
    }, [getAgent, store.loginStore, store.agentsStore]);
    const handleDeleteNote = useCallback((note) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            yield store.chatNotesStore.deleteChatNote(String(note._id));
        }
        catch (error) {
            message.error(React.createElement(Trans, { i18nKey: "noteDeleteError" }));
            console.error(`Couldn't delete the chat note: ${error.message}`);
        }
        finally {
            setLoading(false);
        }
    }), [store.chatNotesStore]);
    useEffect(() => {
        const scrollToNoteDisposer = reaction(() => chatNotesStore.selectedNoteId, (newSelectedNoteId) => {
            if (newSelectedNoteId) {
                const noteElement = document.getElementById(`chat-note-${newSelectedNoteId}`);
                if (noteElement) {
                    if (collapseNotes) {
                        setCollapseNotes(false);
                    }
                    noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                setHighlightedNoteId(newSelectedNoteId);
                setTimeout(() => {
                    setHighlightedNoteId(null);
                    chatNotesStore.setSelectedNoteId(null);
                }, 1500);
            }
            else {
                setHighlightedNoteId(null);
            }
        });
        const selectedMessageIdDisposer = reaction(() => chatNotesStore.selectedMessageId, (newSelectedMessageId) => {
            var _a;
            if (newSelectedMessageId) {
                if (collapseNotes) {
                    setCollapseNotes(false);
                }
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        });
        return () => {
            scrollToNoteDisposer();
            selectedMessageIdDisposer();
        };
    }, [chatNotesStore, collapseNotes]);
    if (!activeChatStore.chat) {
        return null;
    }
    const isInactive = !activeChatStore.isChatActive;
    const noteInput = (React.createElement(ChatNoteInput, { noteText: noteText, loading: loading, handleSetText: handleChangeNoteText, handleAddNote: editingNote ? handleUpdateNote : handleAddNote, inputRef: inputRef, selectedMessageId: chatNotesStore.selectedMessageId, handleRemoveReference: () => {
            chatNotesStore.setSelectedMessageId(null);
        }, editingNote: editingNote, handleCancelEdit: handleCancelEdit, disabled: isInactive }));
    return (React.createElement("div", { className: styles.chatNotesContainer, ref: notesContainerRef },
        React.createElement(ChatNotesHeader, { collapseNotes: collapseNotes, toggleCollapseNotes: toggleCollapseNotes }),
        !collapseNotes && (React.createElement(React.Fragment, null,
            isInactive ? (React.createElement(Tooltip, { title: React.createElement(Trans, { i18nKey: "sidePanelInactiveChatTooltip" }), placement: "top" },
                React.createElement("div", null, noteInput))) : (noteInput),
            React.createElement(ChatNotesList, { loading: chatNotesStore.notesListLoading, notesList: chatNotesStore.notesList, getAgent: getAgent, hasPermission: checkAgentPermission, handleDeleteNote: handleDeleteNote, handleOnClickNote: handleOnClickNote, highlightedNoteId: highlightedNoteId, handleEditNote: handleSelectNoteToEdit, editingNoteId: editingNote ? String(editingNote._id) : null })))));
};
export default observer(ChatNotes);
